import { PASSCODE_LIST_TYPE, PasscodeList } from '@/types/registration.types';
import { PasscodeListMemberSort } from '../../types';
import { SortDirection } from '@ui/buttons/sort-button/types';
import { PasscodeListMemberSortToOrderOptionsEnum } from '../../utils';
import { useSearchParams } from 'react-router-dom';
import TableHead from '@layouts/layout-table/layout-table-head';
import TableRow from '@layouts/layout-table/layout-table-row';
import SortButton from '@ui/buttons/sort-button/sort-button';
import OptionalComponent from '@ui/optional-component/optional-component';
import Checkbox from '@ui/checkbox/checkbox';
import { ICONS } from '@ui/icons/types';

type PasscodelistMemberTabeHeaderProps = {
  passcodeList: PasscodeList;
  sort: PasscodeListMemberSort | undefined;
  setSort: (sort: PasscodeListMemberSort | undefined) => void;
  isAllSelected: boolean;
  selectAll: () => void;
};

const getSortState = (sortBy: PasscodeListMemberSort['sortBy'], sort: PasscodeListMemberSort | undefined) => {
  if (sort?.sortBy === sortBy) {
    return sort.sortOrder === 'asc' ? SortDirection.up : SortDirection.down;
  }

  return SortDirection.none;
};

const PasscodeListMemberTableHeader = ({
  passcodeList,
  sort,
  isAllSelected,
  selectAll,
  setSort,
}: PasscodelistMemberTabeHeaderProps) => {
  const [params, setParams] = useSearchParams();

  const clickSort = (sortBy: PasscodeListMemberSort['sortBy']) => () => {
    let newSort: PasscodeListMemberSort | null = {
      sortBy: sortBy,
      sortOrder: 'asc',
    };

    // new option, default to selecting this option in ascending order
    if (!sort || sort.sortBy !== sortBy) {
      params.set('sort', PasscodeListMemberSortToOrderOptionsEnum(newSort));
    } else {
      const isAsc = sort.sortOrder === 'asc';
      const isDesc = sort.sortOrder === 'desc';

      // if already in ascending order, switch to descending order
      if (isAsc) {
        newSort.sortOrder = 'desc';
        params.set('sort', PasscodeListMemberSortToOrderOptionsEnum(newSort));
      } else if (isDesc) {
        // if already in descending order, remove sort
        // by going back to default
        newSort = null;
        params.delete('sort');
      }
    }
    setParams(Object.fromEntries(params));
    if (setSort) {
      setSort(newSort || undefined);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      selectAll();
    }
  };

  return (
    <TableHead
      className="passcode-list-member-table-header"
      style={{ borderTop: 'none', position: 'sticky', top: 0, backgroundColor: 'var(--gray-1000)', zIndex: 2 }}
    >
      <TableRow>
        <th>
          <Checkbox
            checked={isAllSelected}
            onChange={handleSelectAll}
            value={isAllSelected ? 'all' : ''}
            selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
            unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
          />
        </th>
        <th>
          <div className="flex ai-center">
            <span>PROFILE INFO</span>{' '}
            <SortButton onClick={clickSort('email')} sortDirection={getSortState('email', sort)} />
          </div>
        </th>
        <OptionalComponent
          display={[PASSCODE_LIST_TYPE.PASSCODES_ONLY, PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES].includes(
            passcodeList.type
          )}
        >
          <th>
            <div className="flex ai-center">
              <span>PASSCODE</span>{' '}
              <SortButton onClick={clickSort('passcode')} sortDirection={getSortState('passcode', sort)} />
            </div>
          </th>
        </OptionalComponent>
        <OptionalComponent
          display={[
            PASSCODE_LIST_TYPE.EMAILS_ONLY,
            PASSCODE_LIST_TYPE.PASSCODES_ONLY,
            PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES,
          ].includes(passcodeList.type)}
        >
          <th>
            <div className="flex ai-center">
              <span>USES</span> <SortButton onClick={clickSort('uses')} sortDirection={getSortState('uses', sort)} />
            </div>
          </th>
        </OptionalComponent>

        <th>REGISTRATIONS</th>
        <th>ATTENDED EVENTS</th>
        <OptionalComponent display={[PASSCODE_LIST_TYPE.MAGIC_LINKS].includes(passcodeList.type)}>
          <th>
            <div className="flex ai-center">
              <span>STATUS</span>{' '}
              <SortButton onClick={clickSort('status')} sortDirection={getSortState('status', sort)} />
            </div>
          </th>
        </OptionalComponent>
        <OptionalComponent
          display={[PASSCODE_LIST_TYPE.MAGIC_LINKS, PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION].includes(
            passcodeList.type
          )}
        >
          <th>LOGIN LINK</th>
        </OptionalComponent>
      </TableRow>
    </TableHead>
  );
};
export default PasscodeListMemberTableHeader;
